/**
 * Shared helpers: check pathology BOM stock and deduct at sample collection.
 */

import { getAllInventory, deductInventory } from "../services/inventoryService";
import { getBOMsFromBaseTable } from "../services/labService";
import { getAllLabTests } from "../services/labtestService";
import { recordPathologyLabTestOut } from "../services/pathologyTransactionService";
import {
  evaluatePathBomStockForLabTests,
  hasBlockingPathBomStockIssues,
  PATH_BOM_STOCK_STATUS,
} from "../utils/pathBomStockCheck";

/**
 * Load masters + inventory and evaluate BOM stock for the given patient lab tests.
 */
export const checkPathBomStockForLabTests = async (
  patientLabTests = [],
  { allowMissingExpiry = true, catalogLabTests, boms, inventoryList } = {}
) => {
  const tests = Array.isArray(patientLabTests) ? patientLabTests : [];
  if (tests.length === 0) {
    return {
      checks: [],
      hasBlocking: false,
      labTestIdsToDeduct: [],
      hasBomRequirements: false,
      catalogLabTests: [],
    };
  }

  const [resolvedCatalog, resolvedBoms, resolvedInventory] = await Promise.all([
    catalogLabTests
      ? Promise.resolve(catalogLabTests)
      : getAllLabTests().catch(() => []),
    boms ? Promise.resolve(boms) : getBOMsFromBaseTable().catch(() => []),
    inventoryList
      ? Promise.resolve(inventoryList)
      : getAllInventory().catch(() => []),
  ]);

  return evaluatePathBomStockForLabTests({
    patientLabTests: tests,
    catalogLabTests: resolvedCatalog,
    boms: resolvedBoms,
    inventoryList: resolvedInventory,
    allowMissingExpiry,
  });
};

/**
 * Deduct pathology inventory for each catalog lab-test id and record OUT history.
 * Throws if any deduct call fails (so sample collection can abort).
 */
export const deductPathInventoryForLabTests = async ({
  labTestIds = [],
  patientContextByLabTestId = {},
}) => {
  const ids = [
    ...new Set(
      (Array.isArray(labTestIds) ? labTestIds : [])
        .map((id) => Number(id))
        .filter((id) => Number.isFinite(id) && id > 0)
    ),
  ];

  const results = [];
  for (const labTestId of ids) {
    const ctx = patientContextByLabTestId[labTestId] || {};
    await deductInventory(labTestId);
    await recordPathologyLabTestOut({
      labTestId,
      labTestName: ctx.labTestName,
      patientId: ctx.patientId,
      patientName: ctx.patientName,
      patientType: ctx.patientType,
      doctorName: ctx.doctorName,
      doctorId: ctx.doctorId,
      prescriptionId: ctx.prescriptionId,
      invoiceId: ctx.invoiceId,
      reportId: ctx.reportId,
      admissionId: ctx.admissionId,
      userId: ctx.userId,
    });
    results.push(labTestId);
  }
  return results;
};

/**
 * Pre-collection gate: check stock; if OK and BOMs exist, deduct then return.
 * @returns {{ ok: true, checks, labTestIdsToDeduct } | { ok: false, checks, reason }}
 */
export const assertPathBomStockAndDeductForCollection = async ({
  patientLabTests = [],
  patientContextByLabTestId = {},
  allowMissingExpiry = true,
  catalogLabTests,
}) => {
  const evaluation = await checkPathBomStockForLabTests(patientLabTests, {
    allowMissingExpiry,
    catalogLabTests,
  });

  if (evaluation.hasBlocking) {
    return {
      ok: false,
      reason: "insufficient_stock",
      checks: evaluation.checks,
      labTestIdsToDeduct: evaluation.labTestIdsToDeduct,
    };
  }

  if (evaluation.hasBomRequirements && evaluation.labTestIdsToDeduct.length > 0) {
    try {
      await deductPathInventoryForLabTests({
        labTestIds: evaluation.labTestIdsToDeduct,
        patientContextByLabTestId,
      });
    } catch (error) {
      const message =
        error?.response?.data?.message ||
        error?.response?.data ||
        error?.message ||
        "Inventory deduction failed.";
      return {
        ok: false,
        reason: "deduct_failed",
        checks: evaluation.checks,
        labTestIdsToDeduct: evaluation.labTestIdsToDeduct,
        errorMessage: typeof message === "string" ? message : "Inventory deduction failed.",
        error,
      };
    }
  }

  return {
    ok: true,
    checks: evaluation.checks,
    labTestIdsToDeduct: evaluation.labTestIdsToDeduct,
    hasBomRequirements: evaluation.hasBomRequirements,
  };
};

/**
 * Result-entry gate (no inventory deduct — deduct happens at sample collection).
 *
 * - Editing an already-COMPLETE result: caller should skip this entirely.
 * - After COLLECTED: qty may already be reduced; only block hard misconfig
 *   (never in inventory / invalid qty). Insufficient/out-of-stock after collect
 *   is expected and must not block result entry.
 * - If not yet collected (legacy / edge path): full stock gate like collect.
 */
export const assertPathBomStockForResultEntry = async ({
  patientLabTests = [],
  alreadyCollected = true,
  allowMissingExpiry = true,
  catalogLabTests,
}) => {
  const evaluation = await checkPathBomStockForLabTests(patientLabTests, {
    allowMissingExpiry,
    catalogLabTests,
  });

  if (!evaluation.hasBomRequirements) {
    return { ok: true, checks: [], skipped: true };
  }

  if (alreadyCollected) {
    const hardBlocks = evaluation.checks.filter(
      (c) =>
        c.status === PATH_BOM_STOCK_STATUS.NOT_IN_INVENTORY ||
        c.status === PATH_BOM_STOCK_STATUS.INVALID_QTY
    );
    if (hardBlocks.length > 0) {
      return {
        ok: false,
        reason: "insufficient_stock",
        checks: hardBlocks,
      };
    }
    return { ok: true, checks: evaluation.checks, skippedQtyGate: true };
  }

  if (hasBlockingPathBomStockIssues(evaluation.checks)) {
    return {
      ok: false,
      reason: "insufficient_stock",
      checks: evaluation.checks,
    };
  }

  return { ok: true, checks: evaluation.checks };
};

export { hasBlockingPathBomStockIssues };
